import type { ApolloClient } from '@apollo/client'
import { ApolloLink, Observable } from '@apollo/client'
import { CombinedGraphQLErrors } from '@apollo/client/errors'
import { SetContextLink } from '@apollo/client/link/context'
import { ErrorLink } from '@apollo/client/link/error'
import { RefreshTokenDocument } from '@gql'
import { shouldPersistToken } from '@sections/auth/hooks/useAuthProvider'
import type { Locale } from '@ui/Locale'
import { uuid } from '@utils/uuid'

import { shouldRefreshJwtToken } from './errors'
import { getTokens, removeTokens, setAuthToken } from './utils'

const appSessionId = uuid()

function getAuthorizationHeadersForToken(token: string | null) {
  return {
    authorization: token ? `Bearer ${token}` : null,
  }
}

export function getBaseHeaders(locale: Locale) {
  const authToken = getTokens().auth
  return {
    'x-app-session-id': appSessionId,
    'x-user-locale': locale,
    ...getAuthorizationHeadersForToken(authToken),
  }
}

const createHeadersLinks = (locale: Locale) =>
  new SetContextLink((context, _) => ({
    ...context,
    headers: {
      ...context.headers,
      'Apollo-Require-Preflight': 'true',
      ...getBaseHeaders(locale),
    },
  }))

function refreshTokenPromise(client: ApolloClient, refreshToken: string): Promise<string> {
  if (!client.refreshTokenPromise) {
    client.refreshTokenPromise = new Promise((resolve, reject) => {
      client
        .mutate({
          context: {
            disableRefresh: true,
          },
          mutation: RefreshTokenDocument,
          variables: {
            token: refreshToken,
          },
        })
        .then((result) => {
          const accessToken = result.data?.tokenRefresh.token
          if (!accessToken) {
            reject(new Error('No access token returned from refresh'))
            return
          }

          setAuthToken(accessToken, shouldPersistToken)
          resolve(accessToken)
        })
        .catch((error) => {
          // Only a server verdict on the refresh token ends the session; a
          // network failure (offline, mid-wake fetch) must not wipe tokens or
          // the app silently loses auth until reload — the next operation
          // simply refreshes again.
          if (CombinedGraphQLErrors.is(error)) {
            removeTokens()
          }

          reject(error)
        })
        .finally(() => {
          client.refreshTokenPromise = null
        })
    })
  }

  return client.refreshTokenPromise!
}

/**
 * Refresh the access token outside the link chain (deduped with the in-link
 * refresh via `client.refreshTokenPromise`). Rejects when there is no refresh
 * token or the refresh itself fails — the caller decides what to do then.
 */
export function ensureFreshAccessToken(client: ApolloClient): Promise<string> {
  const refreshToken = getTokens().refresh
  if (!refreshToken) {
    return Promise.reject(new Error('No refresh token available'))
  }

  return refreshTokenPromise(client, refreshToken)
}

const tokenRefreshLink = new ErrorLink(({ error, operation, forward }) => {
  const refreshToken = getTokens().refresh
  if (
    !operation.getContext().disableRefresh &&
    CombinedGraphQLErrors.is(error) &&
    shouldRefreshJwtToken(error.errors) &&
    refreshToken
  ) {
    return new Observable((observer) => {
      refreshTokenPromise(operation.client, refreshToken)
        .then((newAccessToken) => {
          const variableName = operation.getContext().tokenInVariable
          if (variableName) {
            operation.variables[variableName] = newAccessToken
          } else {
            const oldHeaders = operation.getContext().headers
            operation.setContext({
              headers: {
                ...oldHeaders,
                ...getAuthorizationHeadersForToken(newAccessToken),
              },
            })
          }

          forward(operation).subscribe(observer)
        })
        .catch((error) => {
          observer.error(error)
        })
    })
  }

  return undefined
})

export const createAuthLink = (locale: Locale) =>
  ApolloLink.from([createHeadersLinks(locale), tokenRefreshLink])
